export type AppRouteScope = 'app' | 'tenant';

export interface AppRouteContext {
  scope: AppRouteScope;
  tenantId?: string;
  publicProfileFriendlyUrl?: string;
}

const routeContextStoragePrefix = 'hsdesk-route-context';
const defaultRouteContext: AppRouteContext = { scope: 'app' };

let currentRouteContext: AppRouteContext | null = null;

function normalizeHost(host: string) {
  return host
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\.$/, '');
}

function getRouteContextStorageKey() {
  if (typeof window === 'undefined') {
    return routeContextStoragePrefix;
  }

  const host = normalizeHost(window.location.hostname);

  return host
    ? `${routeContextStoragePrefix}:${host}`
    : routeContextStoragePrefix;
}

function readStoredRouteContext(): AppRouteContext | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedValue = window.sessionStorage.getItem(
      getRouteContextStorageKey()
    );

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue) as Partial<AppRouteContext>;

    return parsedValue.scope === 'tenant' || parsedValue.scope === 'app'
      ? {
          scope: parsedValue.scope,
          tenantId: parsedValue.tenantId,
          publicProfileFriendlyUrl: parsedValue.publicProfileFriendlyUrl
        }
      : null;
  } catch {
    return null;
  }
}

export function setRouteContext(context: AppRouteContext) {
  currentRouteContext = context;

  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(
    getRouteContextStorageKey(),
    JSON.stringify(context)
  );
}

export function getRouteContext() {
  return currentRouteContext ?? readStoredRouteContext() ?? defaultRouteContext;
}

export function getRouteScope() {
  return getRouteContext().scope;
}

export function isTenantRouteScope() {
  return getRouteScope() === 'tenant';
}

export function getRouteTenantId() {
  return getRouteContext().tenantId;
}

export function getRoutePublicProfileFriendlyUrl() {
  return getRouteContext().publicProfileFriendlyUrl;
}
